import type { ReactNode } from 'react';

import { Link } from '@/core/i18n/navigation';
import type { ContentPage } from '@/lib/content-registry';
import type { Crumb } from '@/lib/seo';

import { Breadcrumb } from './breadcrumb';

export function QuickAnswer({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <aside className="crt-quick-answer">
      <p className="crt-eyebrow">{label}</p>
      <div>{children}</div>
    </aside>
  );
}
export function GuideCard({
  page,
  answer,
}: {
  page: ContentPage;
  answer?: string;
}) {
  return (
    <Link href={page.url} className="crt-card">
      <h2>{page.h1}</h2>
      <p>{answer || page.description}</p>
    </Link>
  );
}
export function RelatedGuides({
  title,
  pages,
}: {
  title: string;
  pages: ContentPage[];
}) {
  return (
    <section className="crt-related">
      <h2>{title}</h2>
      <ul>
        {pages.map((page) => (
          <li key={page.url}>
            <Link href={page.url}>{page.h1} ↗</Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
export function SourceList({
  title,
  verifiedLabel,
  date,
  dateLabel,
  sources,
}: {
  title: string;
  verifiedLabel: string;
  date: string;
  dateLabel: string;
  sources: { id: string; title: string; url: string }[];
}) {
  return (
    <section className="crt-sources">
      <h2>{title}</h2>
      <p>
        {verifiedLabel}：<time dateTime={date}>{dateLabel}</time>
      </p>
      <ul>
        {sources.map((source) => (
          <li key={source.id}>
            <a href={source.url} target="_blank" rel="noopener noreferrer">
              {source.title} ↗
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
export function ComparisonTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <div
      className="crt-table-wrap"
      tabIndex={0}
      role="region"
      aria-label={headers.join(', ')}
    >
      <table>
        <thead>
          <tr>
            {headers.map((header) => (
              <th scope="col" key={header}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, col) =>
                col === 0 ? (
                  <th scope="row" key={col}>
                    {cell}
                  </th>
                ) : (
                  <td key={col}>{cell}</td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function PuzzleDiagram({
  src,
  alt,
  width,
  height,
  caption,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  caption?: string;
}) {
  return (
    <figure className="crt-diagram">
      <img src={src} alt={alt} width={width} height={height} />
      <figcaption>{caption || alt}</figcaption>
    </figure>
  );
}
export function GuideLayout({
  page,
  wide = false,
  updatedLabel,
  dateLabel,
  children,
  crumbs,
  breadcrumbLabel,
}: {
  page: ContentPage;
  wide?: boolean;
  updatedLabel: string;
  dateLabel: string;
  children: ReactNode;
  crumbs: Crumb[];
  breadcrumbLabel: string;
}) {
  return (
    <main
      id="main-content"
      className={`crt-container crt-guide ${wide ? 'crt-guide-wide' : ''}`}
    >
      <Breadcrumb items={crumbs} label={breadcrumbLabel} />
      <article>
        <header>
          <p className="crt-eyebrow">
            {page.locale === 'zh'
              ? '工具与攻略'
              : `${page.type} / ${page.url.split('/')[1]}`}
          </p>
          <h1>{page.h1}</h1>
          <p className="crt-muted crt-updated">
            {updatedLabel}: <time dateTime={page.updatedAt}>{dateLabel}</time>
          </p>
        </header>
        {children}
      </article>
    </main>
  );
}
